import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { map, shareReplay, tap } from "rxjs/operators";
import { Observable, Subject } from "rxjs";

import { IAppConfiguration } from "@logex/framework/lg-application";

import { MatomoConfiguration } from "@codman/shared/util-tracking";
import {
    AppConfiguration,
    Dashboards,
    EnvironmentSettings,
    IEnvironmentSettingsLayout,
    UserflowUserAttributes,
} from "@codman/shared/types";
import {
    CodmanApplication,
    Environment,
    InsightsApplication,
    TENANT_DOMAINS,
} from "@codman/shared/assets";
import { HelpLink, HelpLinksId, getSupportLinksHeader } from "./help-links";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { APP_CA_INSTANCE_LOOKUP, APP_INSTANCE_LOOKUP } from "./shared-lookups";

const DYNAMIC_LOCALIZATION_APP = "exploration";
const SHARED_REPORTING_DOMAIN = "nl.mrdm.com";
const LOCALHOST_ENVIRONMENT: Environment = "local";
type LocalhostTargetEnvironment = Extract<Environment, "dev" | "test">;
type RuntimeResolvedSettings = EnvironmentSettings & {
    auth0: NonNullable<EnvironmentSettings["auth0"]>;
    authApiUrl: string;
    domain: string;
};
type LocalhostTargetApplication = InsightsApplication | "etl";
type LocalhostApplicationAuth0Settings = Pick<
    RuntimeResolvedSettings["auth0"],
    "audience" | "clientId"
>;

// This value to be changed for localhost development to switch between dev and test envs
const LOCALHOST_TARGET_ENVIRONMENT: LocalhostTargetEnvironment = "test";
const LOCALHOST_TARGET_SETTINGS: Record<
    LocalhostTargetEnvironment,
    {
        auth0ByApplication: Record<LocalhostTargetApplication, LocalhostApplicationAuth0Settings>;
        auth0Domain: RuntimeResolvedSettings["auth0"]["domain"];
        authApiUrl: string;
        domain: string;
        myAccountUrl: string;
    }
> = {
    dev: {
        domain: "nl.logex.cloud",
        auth0Domain: "login-dev.logex.nl",
        auth0ByApplication: {
            "data-request": {
                clientId: "wdBSuh29oug1VtWmpHe675aqJnH8Bw3j",
                audience: "https://api.logex.nl/codman",
            },
            descriptives: {
                clientId: "Oc9ghPcrm3rY9nzXPXJgCzkPQaTMiTag",
                audience: "https://api.logex.nl/codman",
            },
            etl: {
                clientId: "lH9CsA4OlOeQwcCPGLi8MKn6iruaaest",
                audience: "https://api.logex.nl/codman",
            },
            exploration: {
                clientId: "0UYa5I1XAOREXHdzXY4gVyswVe4hAucQ",
                audience: "https://api.logex.nl/codman",
            },
            indicators: {
                clientId: "XDCoMk7WMwjnokoDxAkOOtunw8i6h81f",
                audience: "https://api.logex.nl/codman",
            },
            overview: {
                clientId: "2Nb8fwzScBC3GfL4wZhVJBKaTI5UQ8fw",
                audience: "https://api.logex.nl/codman",
            },
            patients: {
                clientId: "H8HiKkm3csZFZZOwVEChAdPTfzpegtQu",
                audience: "https://api.logex.nl/codman",
            },
            "patients-like-me": {
                clientId: "H8HiKkm3csZFZZOwVEChAdPTfzpegtQu",
                audience: "https://api.logex.nl/codman",
            },
            portal: {
                clientId: "GfqxPcD0dEE9qYnFajl2BSRSn2ZyiJem",
                audience: "https://api.logex.nl/codman",
            },
        },
        authApiUrl: "https://user-authorization-api.dev.nl.logex.com/",
        myAccountUrl: "https://myaccount-dev.mrdm.com/",
    },
    test: {
        domain: "nl.mrdm.com",
        auth0Domain: "accounts-test.mrdm.io",
        auth0ByApplication: {
            "data-request": {
                clientId: "WN4uoyGgiBVJvGsihlZuZLS86mNPWfOg",
                audience: "https://data-request.mrdm.io",
            },
            descriptives: {
                clientId: "oxfGyJHYClbrE6q4WoCqUSimQNB2Mnyl",
                audience: "https://codman.mrdm.io",
            },
            etl: {
                clientId: "DVtmsLHxjPqRSF9RsxSJ95jy3Das193A",
                audience: "https://codman.mrdm.io",
            },
            exploration: {
                clientId: "l70dLxbbbZAV8HfNqAeDs3cSLr9wMkBH",
                audience: "https://codman.mrdm.io",
            },
            indicators: {
                clientId: "WheqnFWhgpqjVIfOovb4lIs2JtOWXfVp",
                audience: "https://codman.mrdm.io",
            },
            overview: {
                clientId: "POCOUcNilGLgk2CHFNuwdcD6HYUG5HEv",
                audience: "https://codman.mrdm.io",
            },
            patients: {
                clientId: "G9rmxEUMm5TLEj6p7QtB6ubi6P1T4GWo",
                audience: "https://codman.mrdm.io",
            },
            "patients-like-me": {
                clientId: "WN4uoyGgiBVJvGsihlZuZLS86mNPWfOg",
                audience: "https://dcra.codman-plm.mrdm.io",
            },
            portal: {
                clientId: "KitkUAztBs5e5aZu99bpQd6lG5IT1FWU",
                audience: "https://codman.mrdm.io",
            },
        },
        authApiUrl: "https://user-authorization-api-test.mrdm.eu/",
        myAccountUrl: "https://myaccount-test.mrdm.com/",
    },
};
export const AUTHORIZATION_API_USER = "api/v3/user";
export const AUTHORIZATION_API_ACCESS = "api/v3/access";

@Injectable({
    providedIn: "root",
})
export class SharedConfigService {
    private _http = inject(HttpClient);
    private _matomoConfiguration = inject(MatomoConfiguration);

    private _sharedApiUrl: string | null = null;
    private _configuration: AppConfiguration | null = null;
    readonly configuration$: Observable<AppConfiguration> = this._http
        .get<EnvironmentSettings>("assets/config/env-settings.json")
        .pipe(
            map(config => {
                const runtimeConfig = this._resolveRuntimeCriticalSettings(config);

                return {
                    ...runtimeConfig,
                    apiUrl: this._getApiUrl(runtimeConfig),
                    overviewApiUrl: this._getOverviewApiUrl(runtimeConfig),
                    sharedApiUrl: this._getSharedApiUrl(runtimeConfig),
                    localizationApiUrl: this._getLocalizationUrl(runtimeConfig),
                    myAccountUrl: this._getMyAccountUrl(runtimeConfig),
                    dotEnv:
                        runtimeConfig.environment === "prod" ? "" : "." + runtimeConfig.environment,
                    dashEnv:
                        runtimeConfig.environment === "prod" ? "" : "-" + runtimeConfig.environment,
                };
            }),
            tap(config => this._initialize(config)),
            shareReplay(1),
        );

    private _layout: IEnvironmentSettingsLayout = {
        sharedUiFooterShowDicaLogo: true,
        sharedUiFooterLocalizationId: "APP._Shared.Footer",
    };

    private _appConfigurationReady$ = new Subject<void>();
    private _appConfiguration: IAppConfiguration = {
        instance: "",
        environment: "",
        applicationInsightsInstrumentationKey: "",
        applicationInsightsExcludeDomains: [],
        userflowEnvironmentKey: "",
        userflowContentId: "",
        logoutUrl: "",
        switchHospital: true,
        switchHospitalUrl: "",
        switchMergedHospitals: true,
        testMachine: "",
        applicationRoot: "",
        ready: this._appConfigurationReady$.toPromise(),
    };

    private readonly _notInitializedErrorMessage = "Configuration has not yet been initialised.";

    _userflowUserAttributes?: UserflowUserAttributes;

    get sharedApiUrl(): string | null {
        return this._sharedApiUrl;
    }

    initialize(): () => Observable<AppConfiguration> {
        return () => this.configuration$;
    }

    getConfiguration(): AppConfiguration {
        if (this._configuration) {
            return this._configuration;
        } else {
            throw new Error(this._notInitializedErrorMessage);
        }
    }

    getCountry(): string {
        const config = this.getConfiguration();
        return config?.domain.split(".")[0].toUpperCase() ?? "";
    }

    getAppInstanceLookUp(app: CodmanApplication): string {
        return this.getCountry() === "CA" ? APP_CA_INSTANCE_LOOKUP[app] : APP_INSTANCE_LOOKUP[app];
    }

    getAppConfiguration(): IAppConfiguration {
        return this._appConfiguration;
    }

    getLayout(): IEnvironmentSettingsLayout {
        return this._layout;
    }

    get userflowUserAttributes(): UserflowUserAttributes {
        if (this._userflowUserAttributes) {
            return this._userflowUserAttributes;
        } else {
            throw new Error(this._notInitializedErrorMessage);
        }
    }

    getApiUrl(application: InsightsApplication): string {
        const config = {
            ...this.getConfiguration(),
            application,
        };
        return this._getApiUrl(config);
    }

    getSupportLinksHeader(
        dashboard: Dashboards | undefined,
        tenant: string | undefined,
        registryId: string | null,
        translateService: LgTranslateService,
    ): Partial<{ [key in HelpLinksId]: HelpLink }> {
        return getSupportLinksHeader(dashboard, tenant, registryId, translateService);
    }

    private _initialize(config: AppConfiguration): void {
        this._configuration = config;
        this._sharedApiUrl = config.sharedApiUrl;
        this._layout = this._getLayout(config);
        this._userflowUserAttributes = this._getUserflowUserAttributes(config);
        this._matomoConfiguration.configure(config);

        this._appConfiguration = this._getAppConfiguration(config);
        this._appConfigurationReady$.next();
        this._appConfigurationReady$.complete();
    }

    private _getApiUrl(config: EnvironmentSettings): string {
        if (config.application === "patients-like-me") return this._getPatientsLikeMeApiUrl(config);
        if (config.application === "patients") return this._getPatientsApiUrl(config);

        return this._buildReportingUrl(`${config.application}-reporting`, config, true);
    }

    private _getPatientsApiUrl(config: EnvironmentSettings): string {
        const patientsConfig =
            config.environment === "dev" ? { ...config, domain: "nl.logex.cloud" } : config;

        return this._buildReportingUrl("patients-reporting", patientsConfig, true);
    }

    private _getPatientsLikeMeApiUrl(config: EnvironmentSettings): string {
        if (config.environment === "prod") {
            return `https://dcra.valuebase.nl/patientslikeme`;
        } else {
            return `https://dcra-${config.environment}.valuebase.nl/patientslikeme`;
        }
    }

    private _getOverviewApiUrl(config: EnvironmentSettings): string {
        return this._buildReportingUrl("overview-reporting", config, true);
    }

    private _getSharedApiUrl(config: EnvironmentSettings): string {
        if (config.sharedApiUrl) {
            return config.sharedApiUrl;
        }

        return this._buildReportingUrl(
            "shared-reporting",
            { ...config, domain: SHARED_REPORTING_DOMAIN },
            false,
        );
    }

    private _getLocalizationUrl(config: EnvironmentSettings): string {
        if (config.localizationApiUrl) {
            return config.localizationApiUrl;
        }

        if (config.application === "patients") {
            return this._getPatientsApiUrl(config);
        }

        const localizationApplication = DYNAMIC_LOCALIZATION_APP;

        return this._buildReportingUrl(`${localizationApplication}-reporting`, config, true);
    }

    private _buildReportingUrl(
        subdomain: string,
        config: EnvironmentSettings,
        includeTenant: boolean,
    ): string {
        const dotTenant = includeTenant && config.tenant ? `.${config.tenant}` : "";
        const dotEnvironment = config.environment === "prod" ? "" : `.${config.environment}`;

        return `https://${subdomain}${dotTenant}${dotEnvironment}.${config.domain}`;
    }

    private _getMyAccountUrl(config: EnvironmentSettings): string {
        if (config.myAccountUrl) {
            return config.myAccountUrl;
        }

        if (config.environment === "prod") {
            return `https://myaccount.mrdm.com/`;
        } else if (config.environment === "staging") {
            return `https://myaccount-accept.mrdm.com/`;
        } else {
            return `https://myaccount-${config.environment}.mrdm.com/`;
        }
    }

    private _applyLocalhostTargetConfig(config: EnvironmentSettings): EnvironmentSettings {
        if (config.environment !== LOCALHOST_ENVIRONMENT) {
            return config;
        }

        const targetConfig = LOCALHOST_TARGET_SETTINGS[LOCALHOST_TARGET_ENVIRONMENT];
        const targetAuth0Config =
            targetConfig.auth0ByApplication[config.application as LocalhostTargetApplication];

        if (!targetAuth0Config) {
            throw new Error(
                `No localhost Auth0 configuration defined for application '${config.application}'.`,
            );
        }

        return {
            ...config,
            environment: LOCALHOST_TARGET_ENVIRONMENT,
            domain: this._getRuntimeDomain(config, targetConfig.domain),
            authApiUrl: targetConfig.authApiUrl,
            myAccountUrl: config.myAccountUrl ?? targetConfig.myAccountUrl,
            auth0: {
                ...targetAuth0Config,
                domain: targetConfig.auth0Domain,
            },
        };
    }

    private _getRuntimeDomain(config: EnvironmentSettings, fallbackDomain: string): string {
        return (
            TENANT_DOMAINS.find(tenantDomain => tenantDomain.tenant.includes(config.tenant ?? ""))
                ?.domain ?? fallbackDomain
        );
    }

    private _resolveRuntimeCriticalSettings(config: EnvironmentSettings): RuntimeResolvedSettings {
        const runtimeConfig = this._applyLocalhostTargetConfig(config);

        if (!runtimeConfig.domain) {
            throw new Error(
                "Missing 'domain' in env-settings.json. This value must be injected by deployment or set via localhost target settings.",
            );
        }

        if (!runtimeConfig.authApiUrl) {
            throw new Error(
                "Missing 'authApiUrl' in env-settings.json. This value must be injected by deployment or set via localhost target settings.",
            );
        }

        if (
            !runtimeConfig.auth0?.domain ||
            !runtimeConfig.auth0?.clientId ||
            !runtimeConfig.auth0?.audience
        ) {
            throw new Error(
                "Missing required 'auth0' settings (domain, clientId, audience) in env-settings.json. These values must be injected by deployment or set via localhost target settings.",
            );
        }

        return {
            ...runtimeConfig,
            domain: runtimeConfig.domain,
            authApiUrl: runtimeConfig.authApiUrl,
            auth0: runtimeConfig.auth0,
        };
    }

    private _getAppConfiguration(config: AppConfiguration): IAppConfiguration {
        return {
            ...this._appConfiguration,
            userflowEnvironmentKey: config?.userflow?.environmentKey,
            userflowContentId: config?.userflow?.contentId,
            environment: config.environment,
            instance: config.application,
            applicationRoot: config.apiUrl,
        };
    }

    private _getLayout(config: EnvironmentSettings): IEnvironmentSettingsLayout {
        const layout = config.layout ?? null;

        return {
            ...this._layout,
            ...layout,
        };
    }

    private _getUserflowUserAttributes({
        dotEnv,
        tenant,
    }: AppConfiguration): UserflowUserAttributes {
        return {
            dotEnv,
            tenant,
            tenantAbbreviation: tenant?.toUpperCase(),
        };
    }
}
